<script setup lang="ts">
import type {
  ExternalCourse,
  ExternalSubject,
  SubjectRequest,
} from '@/models/current-student-advanced-standing-form/api'
import type {
  PrecedentOutcome,
  SubjectPrecedentTableRow,
} from '@/models/current-student-advanced-standing-form/precedents'

defineProps<{
  externalCourses: ExternalCourse[]
  courseSubjects: (course: ExternalCourse) => ExternalSubject[]
  subjectPrecedentRows: (subject: ExternalSubject) => SubjectPrecedentTableRow[]
  subjectDisplayCode: (subject: ExternalSubject) => string
  outcomePriorityClass: (outcome: PrecedentOutcome) => string
}>()

defineSlots<{
  courseAfterSubjectPrecedents(props: { course: ExternalCourse }): unknown
}>()

const emit = defineEmits<{
  openSubjectRequest: [subject: ExternalSubject]
  removeSubjectRequest: [subject: ExternalSubject, subjectRequest: SubjectRequest]
}>()
</script>

<template>
  <div id="ltuSubjectsDynamicDiv">
    <div v-if="externalCourses.length === 0" class="alert alert-info">No courses provided</div>
    <div v-for="course in externalCourses" :key="course.external_course_id" class="form-panel">
      <h5>{{ course.external_course_institution?.name ?? course.external_course_institution_name }}</h5>
      <h6>
        {{ course.external_course_name }}
      </h6>
      <table class="table PrecedentsTable"
        v-if="course.external_course_level_has_subjects !== false || courseSubjects(course).length">
        <thead>
          <tr>
            <th>Previous Subject Code</th>
            <th>La Trobe Subject Code</th>
            <th>La Trobe Subject Name</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="courseSubjects(course).length === 0">
            <td colspan="5" class="text-center">== No Subjects Provided ==</td>
          </tr>
          <template v-for="subject in courseSubjects(course)" :key="subject.external_subject_id">
            <tr v-for="row in subjectPrecedentRows(subject)" :key="row.key">
              <td v-if="row.showSubjectCode" :rowspan="row.subjectRowspan">{{ subjectDisplayCode(subject) }}</td>
              <template v-if="row.kind === 'empty'">
                <td colspan="4" class="NoExistingPrecedent">
                  No existing precedents found. If you would like to receive credit using this subject, click the button
                  below.
                </td>
              </template>
              <template v-else-if="row.kind === 'outcome'">
                <td class="OutcomeBox" :class="outcomePriorityClass(row.outcome)" :title="row.outcome.title">
                  {{ row.outcome.subject_code }}
                </td>
                <td colspan="2">{{ row.displaySubjectName }}</td>
              </template>
              <template v-else-if="row.kind === 'manual-request'">
                <td class="SubjectNeedsApproval">{{ row.subjectRequest.subject_code }} *</td>
                <td class="SubjectNeedsApproval">{{ row.displaySubjectName }}</td>
                <td class="SubjectNeedsApproval">
                  <button class="btn btn-danger btn-sm" type="button"
                    @click="emit('removeSubjectRequest', subject, row.subjectRequest)">
                    Remove
                  </button>
                </td>
              </template>
              <template v-else>
                <td colspan="3">
                  <button class="btn btn-sm btn-primary requestSubjectBtn" title="Request a specific La Trobe Subject"
                    type="button" @click="emit('openSubjectRequest', subject)">
                    Request a different La Trobe subject
                  </button>
                </td>
              </template>
            </tr>
          </template>
        </tbody>
      </table>
      <slot name="courseAfterSubjectPrecedents" :course="course"></slot>
    </div>
  </div>
</template>
